import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid2 as Grid, TextField,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Alert, CircularProgress, Tooltip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tabs, Tab, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
} from '@mui/material';
import { ArrowBack, Edit, Add, Save, Cancel, AccountCircle, Lock, ContentCopy, AccountBalanceWallet, AddCard, LockReset, Settings, PeopleAlt } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../services/api';
import { todayCv } from '../../../utils/date';

// Field names match MySQL column names returned by the API
interface AgenteDetalhe {
  ID: string; CODIGO: string; NOME: string; NIF: string; TIPO: string;
  TELEFONE: string; EMAIL: string; MORADA: string; ILHA: string;
  MUNICIPIO: string; LOCALIDADE: string; IBAN: string; ESTADO: string;
  DT_REGISTO: string; USER_ID: string | null;
}
interface Contrato {
  ID: string; NUMERO: string; DT_INICIO: string; DT_FIM: string | null;
  TIPO_COMISSAO: 'FIXA' | 'PERCENTAGEM'; VALOR_COMISSAO: number;
  LIMITE_DIARIO: number | null; LIMITE_TRANSACAO: number | null; ESTADO: string;
}
interface Transacao {
  ID: string; DT_TRANSACAO: string; ENTIDADE_CODIGO: string;
  REFERENCIA: string; MONTANTE: number; COMISSAO_VALOR: number; ESTADO: string;
}
interface WalletInfo {
  SALDO: number; SALDO_MINIMO: number; TOTAL_CARREGADO: number;
  TOTAL_PROCESSADO: number; ESTADO: string;
}
interface WalletMovimento {
  ID: string; TIPO: string; VALOR: number; SALDO_ANTES: number;
  SALDO_DEPOIS: number; DESCRICAO: string; DT_MOVIMENTO: string;
}
interface FaturaComissao {
  ID: string; NUMERO: string; PERIODO_MES: number; PERIODO_ANO: number;
  TOTAL_TRANSACOES: number; TOTAL_VOLUME: number; TOTAL_COMISSAO: number;
  TOTAL_IVA: number; TOTAL_PAGAR: number; ESTADO: string;
  DT_EMISSAO: string; DT_VENCIMENTO: string;
}
interface TaxaServico {
  ID?: string; TIPO_SERVICO: string; TIPO_COMISSAO: string;
  VALOR_COMISSAO: number; TIPO_RECORRENCIA: string; ATIVO: number;
}
interface AgenteCliente {
  ID: string; TIPO_SERVICO: string; CLIENTE_REF: string;
  CLIENTE_NOME: string | null; CLIENTE_EMAIL: string | null;
  ESTADO: string; DT_INICIO: string;
  totalTransacoes: number; totalVolume: number; totalComissao: number;
  ultimaTransacao: string | null;
}

const SERVICO_LABEL: Record<string, string> = {
  PAGAMENTO_SISP: 'Pagamentos SISP',
  EVENTS_INSCRICAO: 'Eventos/Conferências',
  WAG_SUBSCRICAO: 'Subscrições WAG',
  IFUNDHERE: 'iFundHere',
};
const SERVICO_COLOR: Record<string, any> = {
  PAGAMENTO_SISP: 'primary', EVENTS_INSCRICAO: 'secondary',
  WAG_SUBSCRICAO: 'info', IFUNDHERE: 'warning',
};
const ALL_SERVICOS = ['PAGAMENTO_SISP', 'EVENTS_INSCRICAO', 'WAG_SUBSCRICAO', 'IFUNDHERE'];
const defaultTaxas = (): TaxaServico[] => ALL_SERVICOS.map(s => ({
  TIPO_SERVICO: s, TIPO_COMISSAO: 'PERCENTAGEM', VALOR_COMISSAO: 0,
  TIPO_RECORRENCIA: 'ANGARIACAO', ATIVO: 0,
}));

const ESTADO_COLOR: Record<string, any> = {
  PENDENTE: 'warning', ATIVO: 'success', SUSPENSO: 'error', INATIVO: 'default',
  PROCESSADO: 'success', FALHOU: 'error',
  EMITIDA: 'info', PAGA: 'success', RASCUNHO: 'default', CANCELADA: 'error',
};

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const emptyContrato = {
  dtInicio: todayCv(),
  dtFim: '', tipoComissao: 'PERCENTAGEM', valorComissao: '',
  limiteDiario: '', limiteTransacao: '', indeterminado: true,
};

export default function AgenteDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [agente, setAgente] = useState<AgenteDetalhe | null>(null);
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [faturas, setFaturas] = useState<FaturaComissao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [contratoDialog, setContratoDialog] = useState(false);
  const [savingContrato, setSavingContrato] = useState(false);
  const [contratoForm, setContratoForm] = useState({ ...emptyContrato });
  const [estadoDialog, setEstadoDialog] = useState(false);
  const [novoEstado, setNovoEstado] = useState('');
  const [savingEstado, setSavingEstado] = useState(false);
  const [acessoResult, setAcessoResult] = useState<{ username: string; tempPassword: string } | null>(null);
  const [creatingAcesso, setCreatingAcesso] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [walletMovimentos, setWalletMovimentos] = useState<WalletMovimento[]>([]);
  const [depositoDialog, setDepositoDialog] = useState(false);
  const [minimoDialog, setMinimoDialog] = useState(false);
  const [minimoValor, setMinimoValor] = useState('');
  const [minimoSaving, setMinimoSaving] = useState(false);
  const [depositoValor, setDepositoValor] = useState('');
  const [depositoDesc, setDepositoDesc] = useState('');
  const [savingDeposito, setSavingDeposito] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ nome: '', nif: '', telefone: '', email: '', morada: '', ilha: '', municipio: '', localidade: '', iban: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  // Taxas por serviço
  const [taxasDialog, setTaxasDialog] = useState(false);
  const [taxasContratoId, setTaxasContratoId] = useState<string | null>(null);
  const [taxas, setTaxas] = useState<TaxaServico[]>(defaultTaxas());
  const [savingTaxas, setSavingTaxas] = useState(false);
  // Clientes
  const [clientes, setClientes] = useState<AgenteCliente[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(false);

  const fetchAgente = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [aRes, cRes, tRes, fRes, wRes] = await Promise.allSettled([
        api.get(`/admin/agentes/${id}`),
        api.get(`/admin/agentes/${id}/contratos`),
        api.get(`/admin/agentes/${id}/transacoes`, { params: { limit: 50 } }),
        api.get(`/admin/agentes/${id}/comissoes`),
        api.get(`/admin/agentes/${id}/wallet`),
      ]);
      if (aRes.status === 'fulfilled') setAgente(aRes.value.data?.data || null);
      if (cRes.status === 'fulfilled') setContratos(cRes.value.data?.data || []);
      if (tRes.status === 'fulfilled') setTransacoes(tRes.value.data?.data || []);
      if (fRes.status === 'fulfilled') setFaturas(fRes.value.data?.data || []);
      if (wRes.status === 'fulfilled') {
        setWallet(wRes.value.data?.data?.wallet || null);
        setWalletMovimentos(wRes.value.data?.data?.movimentos || []);
      }
      if (aRes.status === 'rejected') throw new Error(aRes.reason?.message);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar agente');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAgente(); }, [fetchAgente]);

  const handleCreateContrato = async () => {
    setSavingContrato(true);
    try {
      await api.post(`/admin/agentes/${id}/contratos`, {
        dtInicio: contratoForm.dtInicio,
        dtFim: contratoForm.indeterminado ? null : contratoForm.dtFim || null,
        tipoComissao: contratoForm.tipoComissao,
        valorComissao: Number(contratoForm.valorComissao),
        limiteDiario: contratoForm.limiteDiario ? Number(contratoForm.limiteDiario) : null,
        limiteTransacao: contratoForm.limiteTransacao ? Number(contratoForm.limiteTransacao) : null,
      });
      setContratoDialog(false);
      setContratoForm({ ...emptyContrato });
      setSuccess('Contrato criado com sucesso.');
      fetchAgente();
    } catch (err: any) {
      setError(err.message || 'Erro ao criar contrato');
    } finally {
      setSavingContrato(false);
    }
  };

  const handleChangeEstado = async () => {
    setSavingEstado(true);
    try {
      await api.put(`/admin/agentes/${id}/estado`, { estado: novoEstado });
      setEstadoDialog(false);
      setSuccess(`Estado alterado para ${novoEstado}.`);
      fetchAgente();
    } catch (err: any) {
      setError(err.message || 'Erro ao alterar estado');
    } finally {
      setSavingEstado(false);
    }
  };

  const handleDeposito = async () => {
    setSavingDeposito(true);
    try {
      await api.post(`/admin/agentes/${id}/wallet/deposito`, {
        valor: Number(depositoValor),
        descricao: depositoDesc || undefined,
      });
      setDepositoDialog(false);
      setDepositoValor('');
      setDepositoDesc('');
      setSuccess('Depósito efectuado com sucesso.');
      fetchAgente();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao efectuar depósito');
    } finally {
      setSavingDeposito(false);
    }
  };

  const handleDefinirMinimo = async () => {
    setMinimoSaving(true);
    try {
      const { data } = await api.put(`/admin/agentes/${id}/wallet/minimo`, {
        saldo_minimo: Number(minimoValor),
      });
      setMinimoDialog(false);
      // O servidor avisa quando a reserva fica acima do saldo — é melhor
      // dizê-lo já do que deixar o agente descobrir no primeiro carregamento
      setSuccess(data?.data?.aviso ?? data?.message ?? 'Reserva actualizada.');
      fetchAgente();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao definir a reserva');
    } finally {
      setMinimoSaving(false);
    }
  };

  const handleResetPassword = async () => {
    setResettingPassword(true);
    try {
      const res = await api.post(`/admin/agentes/${id}/reset-password`);
      setAcessoResult(res.data?.data);
      setSuccess('Password redefinida com sucesso!');
    } catch (err: any) {
      const d = err.response?.data;
      setError((d?.error || err.message) + (d?.detail ? ` (${d.detail})` : ''));
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCriarAcesso = async () => {
    setCreatingAcesso(true);
    try {
      const res = await api.post(`/admin/agentes/${id}/criar-acesso`);
      setAcessoResult(res.data?.data);
      setSuccess('Acesso ao portal criado com sucesso!');
      fetchAgente();
    } catch (err: any) {
      const d = err.response?.data;
      setError((d?.error || err.message) + (d?.detail ? ` (${d.detail})` : ''));
    } finally {
      setCreatingAcesso(false);
    }
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/admin/agentes/${id}`, editForm);
      setEditMode(false);
      setSuccess('Dados actualizados com sucesso.');
      fetchAgente();
    } catch (err: any) {
      const d = err.response?.data;
      setError(d?.error || err.message || 'Erro ao actualizar agente');
    } finally {
      setSavingEdit(false);
    }
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(n);

  const fmtDate = (d: string) =>
    d ? new Date(d).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
  // Colunas DATE (DT_INICIO/DT_FIM do contrato) chegam a 00:00Z → mostrar em UTC.
  const fmtDia = (d: string) =>
    d ? new Date(d).toLocaleDateString('pt-PT', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  const openTaxasDialog = async (contratoId: string) => {
    setTaxasContratoId(contratoId);
    setTaxas(defaultTaxas());
    setTaxasDialog(true);
    try {
      const res = await api.get(`/admin/agentes/${id}/contratos/${contratoId}/taxas-servico`);
      const existing: TaxaServico[] = res.data?.data || [];
      setTaxas(defaultTaxas().map(def => {
        const found = existing.find(e => e.TIPO_SERVICO === def.TIPO_SERVICO);
        return found ? { ...found } : def;
      }));
    } catch { /* ignore — usa defaults */ }
  };

  const handleSaveTaxas = async () => {
    if (!taxasContratoId) return;
    setSavingTaxas(true);
    try {
      await api.put(`/admin/agentes/${id}/contratos/${taxasContratoId}/taxas-servico`,
        taxas.map(t => ({
          tipoServico: t.TIPO_SERVICO, tipoComissao: t.TIPO_COMISSAO,
          valorComissao: Number(t.VALOR_COMISSAO),
          tipoRecorrencia: t.TIPO_RECORRENCIA, ativo: t.ATIVO === 1,
        })));
      setTaxasDialog(false);
      setSuccess('Taxas de serviço guardadas.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao guardar taxas');
    } finally {
      setSavingTaxas(false);
    }
  };

  const fetchClientes = async () => {
    setLoadingClientes(true);
    try {
      const res = await api.get(`/admin/agentes/${id}/clientes`, { params: { limit: 100 } });
      setClientes(res.data?.data || []);
    } catch { /* ignore */ } finally { setLoadingClientes(false); }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  if (!agente) return <Alert severity="error" sx={{ m: 3 }}>Agente não encontrado.</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton onClick={() => navigate('/management/agentes')}><ArrowBack /></IconButton>
        <Box flex={1}>
          <Typography variant="h5" fontWeight={600}>{agente.NOME}</Typography>
          <Typography variant="body2" color="text.secondary">{agente.CODIGO}</Typography>
        </Box>
        <Chip label={agente.ESTADO} color={ESTADO_COLOR[agente.ESTADO]} />
        {agente.USER_ID ? (
          <>
            <Chip icon={<AccountCircle />} label="Com acesso" color="success" variant="outlined" />
            <Button variant="outlined" color="warning" size="small"
              startIcon={resettingPassword ? <CircularProgress size={14} color="inherit" /> : <LockReset />}
              onClick={handleResetPassword} disabled={resettingPassword}>
              Redefinir Password
            </Button>
          </>
        ) : (
          <Button variant="contained" color="secondary" startIcon={creatingAcesso ? <CircularProgress size={16} color="inherit" /> : <Lock />}
            onClick={handleCriarAcesso} disabled={creatingAcesso}>
            Criar Acesso ao Portal
          </Button>
        )}
        <Button variant="outlined" startIcon={<Edit />}
          onClick={() => { setNovoEstado(agente.ESTADO); setEstadoDialog(true); }}>
          Alterar Estado
        </Button>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Tabs value={tab} onChange={(_, v) => { setTab(v); if (v === 5) fetchClientes(); }} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Dados" />
        <Tab label={`Contratos (${contratos.length})`} />
        <Tab label={`Transacções (${transacoes.length})`} />
        <Tab label={`Comissões (${faturas.length})`} />
        <Tab label={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccountBalanceWallet fontSize="small" />
            <span>Wallet {wallet ? `(${new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(Number(wallet.SALDO))})` : ''}</span>
          </Stack>
        } />
        <Tab label={<Stack direction="row" spacing={0.5} alignItems="center"><PeopleAlt fontSize="small" /><span>Clientes</span></Stack>} />
      </Tabs>

      {/* Tab 0: Dados */}
      {tab === 0 && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="flex-end" mb={2} spacing={1}>
              {editMode ? (
                <>
                  <Button size="small" startIcon={<Cancel />} onClick={() => setEditMode(false)} disabled={savingEdit}>
                    Cancelar
                  </Button>
                  <Button variant="contained" size="small" startIcon={savingEdit ? <CircularProgress size={14} color="inherit" /> : <Save />}
                    onClick={handleSaveEdit} disabled={savingEdit}>
                    Guardar
                  </Button>
                </>
              ) : (
                <Button size="small" variant="outlined" startIcon={<Edit />}
                  onClick={() => {
                    setEditForm({
                      nome: agente.NOME || '',
                      nif: agente.NIF || '',
                      telefone: agente.TELEFONE || '',
                      email: agente.EMAIL || '',
                      morada: agente.MORADA || '',
                      ilha: agente.ILHA || '',
                      municipio: agente.MUNICIPIO || '',
                      localidade: agente.LOCALIDADE || '',
                      iban: agente.IBAN || '',
                    });
                    setEditMode(true);
                  }}>
                  Editar
                </Button>
              )}
            </Stack>
            <Grid container spacing={3}>
              {editMode ? (
                <>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth size="small" label="Nome *" value={editForm.nome}
                      onChange={e => setEditForm(f => ({ ...f, nome: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth size="small" label="NIF" value={editForm.nif}
                      onChange={e => setEditForm(f => ({ ...f, nif: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Tipo</Typography>
                    <Typography variant="body2" fontWeight={500}>{agente.TIPO}</Typography>
                    <Divider sx={{ mt: 1 }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth size="small" label="Email" type="email" value={editForm.email}
                      onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth size="small" label="Telefone" value={editForm.telefone}
                      onChange={e => setEditForm(f => ({ ...f, telefone: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth size="small" label="Ilha" value={editForm.ilha}
                      onChange={e => setEditForm(f => ({ ...f, ilha: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth size="small" label="Município" value={editForm.municipio}
                      onChange={e => setEditForm(f => ({ ...f, municipio: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth size="small" label="Localidade" value={editForm.localidade}
                      onChange={e => setEditForm(f => ({ ...f, localidade: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth size="small" label="Morada" value={editForm.morada}
                      onChange={e => setEditForm(f => ({ ...f, morada: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth size="small" label="IBAN" value={editForm.iban}
                      onChange={e => setEditForm(f => ({ ...f, iban: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Data de Registo</Typography>
                    <Typography variant="body2" fontWeight={500}>{fmtDate(agente.DT_REGISTO)}</Typography>
                    <Divider sx={{ mt: 1 }} />
                  </Grid>
                </>
              ) : (
                <>
                  {[
                    { label: 'Nome', value: agente.NOME },
                    { label: 'NIF', value: agente.NIF },
                    { label: 'Tipo', value: agente.TIPO },
                    { label: 'Email', value: agente.EMAIL },
                    { label: 'Telefone', value: agente.TELEFONE },
                    { label: 'Ilha', value: agente.ILHA },
                    { label: 'Município', value: agente.MUNICIPIO },
                    { label: 'Localidade', value: agente.LOCALIDADE },
                    { label: 'Morada', value: agente.MORADA },
                    { label: 'IBAN', value: agente.IBAN },
                    { label: 'Data de Registo', value: fmtDate(agente.DT_REGISTO) },
                  ].map(({ label, value }) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
                      <Divider sx={{ mt: 1 }} />
                    </Grid>
                  ))}
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tab 1: Contratos */}
      {tab === 1 && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" mb={2}>
            <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setContratoDialog(true)}>
              Novo Contrato
            </Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>Número</strong></TableCell>
                  <TableCell><strong>Início</strong></TableCell>
                  <TableCell><strong>Fim</strong></TableCell>
                  <TableCell><strong>Tipo</strong></TableCell>
                  <TableCell align="right"><strong>Comissão</strong></TableCell>
                  <TableCell align="right"><strong>Lim. Diário</strong></TableCell>
                  <TableCell align="right"><strong>Lim. Trans.</strong></TableCell>
                  <TableCell><strong>Estado</strong></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contratos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Sem contratos. Crie um contrato para o agente poder processar pagamentos.
                    </TableCell>
                  </TableRow>
                ) : contratos.map(c => (
                  <TableRow key={c.ID} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{c.NUMERO}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{fmtDia(c.DT_INICIO)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{c.DT_FIM ? fmtDia(c.DT_FIM) : 'Indeterminado'}</TableCell>
                    <TableCell>
                      <Chip label={c.TIPO_COMISSAO} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12 }}>
                      {c.TIPO_COMISSAO === 'PERCENTAGEM' ? `${c.VALOR_COMISSAO}%` : fmt(c.VALOR_COMISSAO)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12 }}>
                      {c.LIMITE_DIARIO ? fmt(c.LIMITE_DIARIO) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12 }}>
                      {c.LIMITE_TRANSACAO ? fmt(c.LIMITE_TRANSACAO) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label={c.ESTADO} size="small" color={ESTADO_COLOR[c.ESTADO] || 'default'} sx={{ fontSize: 11 }} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Configurar taxas por serviço">
                        <IconButton size="small" onClick={() => openTaxasDialog(c.ID)}>
                          <Settings fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Tab 2: Transacções */}
      {tab === 2 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><strong>Data</strong></TableCell>
                <TableCell><strong>Serviço</strong></TableCell>
                <TableCell><strong>Referência</strong></TableCell>
                <TableCell align="right"><strong>Montante</strong></TableCell>
                <TableCell align="right"><strong>Comissão</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transacoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhuma transacção registada.
                  </TableCell>
                </TableRow>
              ) : (transacoes as any[]).map(t => (
                <TableRow key={t.ID} hover>
                  <TableCell sx={{ fontSize: 12 }}>{fmtDate(t.DT_TRANSACAO)}</TableCell>
                  <TableCell>
                    <Chip
                      label={SERVICO_LABEL[t.TIPO_SERVICO || 'PAGAMENTO_SISP'] || t.TIPO_SERVICO || 'SISP'}
                      size="small"
                      color={SERVICO_COLOR[t.TIPO_SERVICO || 'PAGAMENTO_SISP'] || 'default'}
                      sx={{ fontSize: 10 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{t.REFERENCIA}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(t.MONTANTE)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: 'success.main', fontWeight: 600 }}>
                    {fmt(t.COMISSAO_VALOR)}
                  </TableCell>
                  <TableCell>
                    <Chip label={t.ESTADO} size="small" color={ESTADO_COLOR[t.ESTADO] || 'default'} sx={{ fontSize: 11 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Tab 5: Clientes */}
      {tab === 5 && (
        <Box>
          {loadingClientes ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell><strong>Serviço</strong></TableCell>
                    <TableCell><strong>Referência</strong></TableCell>
                    <TableCell><strong>Nome</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell align="right"><strong>Trans.</strong></TableCell>
                    <TableCell align="right"><strong>Volume</strong></TableCell>
                    <TableCell align="right"><strong>Comissão</strong></TableCell>
                    <TableCell><strong>Desde</strong></TableCell>
                    <TableCell><strong>Estado</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Nenhum cliente associado. Os clientes aparecem automaticamente quando o agente regista uma venda.
                      </TableCell>
                    </TableRow>
                  ) : clientes.map(c => (
                    <TableRow key={c.ID} hover>
                      <TableCell>
                        <Chip label={SERVICO_LABEL[c.TIPO_SERVICO] || c.TIPO_SERVICO} size="small"
                          color={SERVICO_COLOR[c.TIPO_SERVICO] || 'default'} sx={{ fontSize: 10 }} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.CLIENTE_REF?.slice(0, 12)}…</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{c.CLIENTE_NOME || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{c.CLIENTE_EMAIL || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>{c.totalTransacoes}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(c.totalVolume)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, color: 'success.main', fontWeight: 600 }}>
                        {fmt(c.totalComissao)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtDia(c.DT_INICIO)}</TableCell>
                      <TableCell>
                        <Chip label={c.ESTADO} size="small"
                          color={c.ESTADO === 'ATIVO' ? 'success' : 'default'} sx={{ fontSize: 11 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Tab 3: Facturas de Comissão */}
      {tab === 3 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><strong>Número</strong></TableCell>
                <TableCell><strong>Período</strong></TableCell>
                <TableCell align="right"><strong>Trans.</strong></TableCell>
                <TableCell align="right"><strong>Volume</strong></TableCell>
                <TableCell align="right"><strong>Comissão</strong></TableCell>
                <TableCell align="right"><strong>Total</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell><strong>Emissão</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {faturas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Sem facturas. Gere facturas em "Agentes Pagali → Comissões".
                  </TableCell>
                </TableRow>
              ) : faturas.map(f => (
                <TableRow key={f.ID} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.NUMERO}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{MESES[f.PERIODO_MES - 1]} {f.PERIODO_ANO}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{f.TOTAL_TRANSACOES}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(f.TOTAL_VOLUME)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: 'success.main', fontWeight: 600 }}>
                    {fmt(f.TOTAL_COMISSAO)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>{fmt(f.TOTAL_PAGAR)}</TableCell>
                  <TableCell>
                    <Chip label={f.ESTADO} size="small" color={ESTADO_COLOR[f.ESTADO] || 'default'} sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{fmtDate(f.DT_EMISSAO)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Tab 4: Wallet */}
      {tab === 4 && (
        <Box>
          {/* Saldo card */}
          <Grid container spacing={2} mb={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Saldo Actual</Typography>
                  <Typography variant="h5" fontWeight={700} color="primary.main">
                    {wallet ? new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(Number(wallet.SALDO)) : '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Total Carregado</Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {wallet ? new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(Number(wallet.TOTAL_CARREGADO)) : '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Total Processado</Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {wallet ? new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(Number(wallet.TOTAL_PROCESSADO)) : '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {/* Reserva: o float abaixo deste valor deixa de poder carregar
                carteiras. É o que dá margem ao agente em vez de o deixar
                descobrir o limite a meio de um carregamento. */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Reserva mínima</Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {wallet ? new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(Number(wallet.SALDO_MINIMO)) : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Utilizável: {wallet
                      ? new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 })
                          .format(Math.max(0, Number(wallet.SALDO) - Number(wallet.SALDO_MINIMO)))
                      : '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button variant="contained" fullWidth startIcon={<AddCard />}
                onClick={() => setDepositoDialog(true)}>
                Carregar Float
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button variant="outlined" fullWidth
                onClick={() => { setMinimoValor(String(wallet?.SALDO_MINIMO ?? 0)); setMinimoDialog(true); }}>
                Definir reserva
              </Button>
            </Grid>
          </Grid>

          {/* Movimentos */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>Data</strong></TableCell>
                  <TableCell><strong>Tipo</strong></TableCell>
                  <TableCell><strong>Descrição</strong></TableCell>
                  <TableCell align="right"><strong>Valor</strong></TableCell>
                  <TableCell align="right"><strong>Saldo Antes</strong></TableCell>
                  <TableCell align="right"><strong>Saldo Depois</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {walletMovimentos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {wallet ? 'Sem movimentos ainda.' : 'Wallet não criada — clique em "Carregar Float" para inicializar.'}
                    </TableCell>
                  </TableRow>
                ) : walletMovimentos.map(m => (
                  <TableRow key={m.ID} hover>
                    <TableCell sx={{ fontSize: 12 }}>{fmtDate(m.DT_MOVIMENTO)}</TableCell>
                    <TableCell>
                      <Chip
                        label={m.TIPO}
                        size="small"
                        color={m.TIPO === 'DEPOSITO' ? 'success' : m.TIPO === 'COMISSAO' ? 'info' : m.TIPO === 'ESTORNO' ? 'warning' : 'default'}
                        sx={{ fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{m.DESCRICAO}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, color: m.TIPO === 'PAGAMENTO' ? 'error.main' : 'success.main', fontWeight: 500 }}>
                      {m.TIPO === 'PAGAMENTO' ? '−' : '+'}{new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(Number(m.VALOR))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12 }}>{new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(Number(m.SALDO_ANTES))}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 500 }}>{new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(Number(m.SALDO_DEPOIS))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Dialog: Reserva mínima de float */}
      <Dialog open={minimoDialog} onClose={() => setMinimoDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reserva mínima de float</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Abaixo deste valor o posto deixa de poder carregar carteiras. Serve para
              o agente ter margem de aviso em vez de esgotar o float a meio de um
              carregamento.
            </Typography>
            <TextField
              fullWidth size="small" label="Reserva (CVE)" type="number"
              value={minimoValor}
              onChange={e => setMinimoValor(e.target.value)}
              inputProps={{ min: 0, step: 500 }}
              helperText={
                wallet && Number(minimoValor) > Number(wallet.SALDO)
                  ? 'Maior que o saldo actual — o posto fica sem poder carregar até haver depósito.'
                  : wallet
                    ? `Saldo actual: ${new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(Number(wallet.SALDO))}`
                    : undefined
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMinimoDialog(false)} disabled={minimoSaving}>Cancelar</Button>
          <Button variant="contained" onClick={handleDefinirMinimo}
            disabled={minimoSaving || minimoValor === '' || Number(minimoValor) < 0}>
            {minimoSaving ? 'A guardar…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Carregar Float */}
      <Dialog open={depositoDialog} onClose={() => setDepositoDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddCard color="primary" /> Carregar Float
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth size="small" label="Valor (CVE) *" type="number"
              value={depositoValor}
              onChange={e => setDepositoValor(e.target.value)}
              inputProps={{ min: 1, step: 100 }}
            />
            <TextField
              fullWidth size="small" label="Descrição (opcional)"
              value={depositoDesc}
              onChange={e => setDepositoDesc(e.target.value)}
              placeholder="Ex: Transferência bancária #12345"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepositoDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleDeposito}
            disabled={savingDeposito || !depositoValor || Number(depositoValor) <= 0}>
            {savingDeposito ? <CircularProgress size={18} /> : 'Confirmar Depósito'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Taxas por Serviço */}
      <Dialog open={taxasDialog} onClose={() => setTaxasDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Taxas de Comissão por Serviço</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define a taxa e recorrência para cada serviço Pagali. Deixa ATIVO desligado para usar a taxa global do contrato.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><strong>Serviço</strong></TableCell>
                <TableCell><strong>Activo</strong></TableCell>
                <TableCell><strong>Tipo</strong></TableCell>
                <TableCell><strong>Valor</strong></TableCell>
                <TableCell><strong>Recorrência</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {taxas.map((taxa, idx) => (
                <TableRow key={taxa.TIPO_SERVICO}>
                  <TableCell>
                    <Chip label={SERVICO_LABEL[taxa.TIPO_SERVICO]} size="small"
                      color={SERVICO_COLOR[taxa.TIPO_SERVICO] || 'default'} sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell>
                    <Switch size="small" checked={taxa.ATIVO === 1}
                      onChange={e => setTaxas(ts => ts.map((t, i) => i === idx ? { ...t, ATIVO: e.target.checked ? 1 : 0 } : t))} />
                  </TableCell>
                  <TableCell>
                    <Select size="small" value={taxa.TIPO_COMISSAO} disabled={taxa.ATIVO !== 1}
                      onChange={e => setTaxas(ts => ts.map((t, i) => i === idx ? { ...t, TIPO_COMISSAO: e.target.value } : t))}>
                      <MenuItem value="PERCENTAGEM">%</MenuItem>
                      <MenuItem value="FIXA">CVE fixo</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" disabled={taxa.ATIVO !== 1}
                      value={taxa.VALOR_COMISSAO} sx={{ width: 90 }}
                      inputProps={{ min: 0, step: taxa.TIPO_COMISSAO === 'PERCENTAGEM' ? 0.01 : 1 }}
                      onChange={e => setTaxas(ts => ts.map((t, i) => i === idx ? { ...t, VALOR_COMISSAO: Number(e.target.value) } : t))} />
                  </TableCell>
                  <TableCell>
                    <Select size="small" value={taxa.TIPO_RECORRENCIA} disabled={taxa.ATIVO !== 1}
                      onChange={e => setTaxas(ts => ts.map((t, i) => i === idx ? { ...t, TIPO_RECORRENCIA: e.target.value } : t))}>
                      <MenuItem value="ANGARIACAO">Angariação (1x)</MenuItem>
                      <MenuItem value="RECORRENTE">Recorrente</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaxasDialog(false)} startIcon={<Cancel />}>Cancelar</Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSaveTaxas} disabled={savingTaxas}>
            {savingTaxas ? <CircularProgress size={18} /> : 'Guardar Taxas'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Novo Contrato */}
      <Dialog open={contratoDialog} onClose={() => setContratoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Contrato</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Data início *" type="date"
                value={contratoForm.dtInicio} InputLabelProps={{ shrink: true }}
                onChange={e => setContratoForm(f => ({ ...f, dtInicio: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={<Switch checked={contratoForm.indeterminado}
                  onChange={e => setContratoForm(f => ({ ...f, indeterminado: e.target.checked }))} />}
                label="Prazo indeterminado" sx={{ mt: 0.5 }} />
            </Grid>
            {!contratoForm.indeterminado && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Data fim" type="date"
                  value={contratoForm.dtFim} InputLabelProps={{ shrink: true }}
                  onChange={e => setContratoForm(f => ({ ...f, dtFim: e.target.value }))} />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Comissão *</InputLabel>
                <Select value={contratoForm.tipoComissao} label="Tipo de Comissão *"
                  onChange={e => setContratoForm(f => ({ ...f, tipoComissao: e.target.value }))}>
                  <MenuItem value="PERCENTAGEM">Percentagem (%)</MenuItem>
                  <MenuItem value="FIXA">Fixa (CVE)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small"
                label={`Valor * (${contratoForm.tipoComissao === 'PERCENTAGEM' ? '%' : 'CVE'})`}
                type="number" value={contratoForm.valorComissao}
                onChange={e => setContratoForm(f => ({ ...f, valorComissao: e.target.value }))}
                inputProps={{ min: 0, step: contratoForm.tipoComissao === 'PERCENTAGEM' ? 0.01 : 1 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Limite diário (CVE, opcional)" type="number"
                value={contratoForm.limiteDiario}
                onChange={e => setContratoForm(f => ({ ...f, limiteDiario: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Limite por transacção (CVE, opcional)" type="number"
                value={contratoForm.limiteTransacao}
                onChange={e => setContratoForm(f => ({ ...f, limiteTransacao: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContratoDialog(false)} startIcon={<Cancel />}>Cancelar</Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleCreateContrato}
            disabled={savingContrato || !contratoForm.valorComissao}>
            {savingContrato ? <CircularProgress size={18} /> : 'Criar Contrato'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Credenciais de Acesso */}
      <Dialog open={!!acessoResult} onClose={() => setAcessoResult(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Lock color="success" /> Acesso Criado
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copie a password agora — não será mostrada novamente.
          </Alert>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Utilizador (email)</Typography>
              <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                {acessoResult?.username}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Password temporária</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={700} fontFamily="monospace" color="primary">
                  {acessoResult?.tempPassword}
                </Typography>
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(acessoResult?.tempPassword || '')}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setAcessoResult(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Alterar Estado */}
      <Dialog open={estadoDialog} onClose={() => setEstadoDialog(false)}>
        <DialogTitle>Alterar Estado do Agente</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Novo Estado</InputLabel>
            <Select value={novoEstado} label="Novo Estado" onChange={e => setNovoEstado(e.target.value)}>
              {['PENDENTE', 'ATIVO', 'SUSPENSO', 'INATIVO'].map(e => (
                <MenuItem key={e} value={e}>{e}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEstadoDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleChangeEstado} disabled={savingEstado || !novoEstado}>
            {savingEstado ? <CircularProgress size={18} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
